/**
 * useFhirLabel — Consume FHIR resource type → industry terminology mapping.
 *
 * Reads from FhirLabelsContext (injected by portal's FhirLabelsProvider).
 * Falls back to the raw FHIR type name if no context is present.
 *
 * Usage:
 *   const getFhirLabel = useFhirLabel();
 *   getFhirLabel('Patient')    → "Resident"  (ALF vertical)
 *   getFhirLabel('Encounter')  → "Service Visit"
 *   getFhirLabel('UnknownType') → "UnknownType"  (identity fallback)
 *
 * Context is provided by FhirLabelsProvider in the portal App.tsx.
 * Subgraph packages (subgraph-fhir, subgraph-payroll, etc.) consume this
 * hook without needing Apollo Client access.
 */

import { createContext, useContext } from 'react';

export type FhirLabelResolver = (fhirResourceType: string) => string;

/** Default resolver: identity (no mapping — raw FHIR type returned). */
const defaultResolver: FhirLabelResolver = (type) => type;

export const FhirLabelsContext = createContext<FhirLabelResolver>(defaultResolver);

/** Resolve a FHIR resource type to its industry display term. */
export const useFhirLabel = (): FhirLabelResolver => useContext(FhirLabelsContext);
